import api from '../api';

const { sessionStorage } = window;
const STORAGE_DOQBOARD_TOKEN = 'doqboard-token';
const STORAGE_USER_ID = 'user-id';

/**
 * Two-Factor Authentication Service
 * Handles all 2FA-related API calls
 */
const twoFactorService = {
  /**
   * Check if 2FA is enabled for the current user
   * @returns {Promise<Object>} Status object with enabled and method properties
   */
  checkStatus: async () => (
    api.requestData('2fa/status', undefined, 'get')
  ),

  /**
   * Initialize 2FA setup
   * @returns {Promise<Object>} Setup data with QR code, secret, and backup codes
   */
  setup: async () => (
    api.requestData('2fa/setup', undefined, 'post')
  ),

  /**
   * Confirm and activate 2FA with a verification token
   * @param {string} token - 6-digit verification code from authenticator app
   * @returns {Promise<Object>} Confirmation result
   */
  confirm: async (token) => (
    api.requestData('2fa/confirm', undefined, 'post', undefined, { token })
  ),

  /**
   * Disable 2FA (requires password confirmation)
   * @param {string} password - User's password
   * @returns {Promise<Object>} Result of disable operation
   */
  disable: async (password) => (
    api.requestData('2fa/disable', undefined, 'post', undefined, { password })
  ),

  /**
   * Regenerate backup codes (requires password confirmation)
   * @param {string} password - User's password
   * @returns {Promise<Object>} New backup codes
   */
  regenerateBackupCodes: async (password) => (
    api.requestData('2fa/backup-codes/regenerate', undefined, 'post', undefined, { password })
  ),

  /**
   * Verify 2FA token during login
   * @param {string} token - 6-digit code or backup code
   * @param {string} tempToken - Temporary authentication token
   * @param {boolean} trustDevice - Whether to trust this device for 30 days
   * @param {string} deviceName - Optional name for the device
   * @returns {Promise<Object>} User object after successful verification
   */
  verify: async (token, tempToken, trustDevice = false, deviceName = '') => {
    const result = await api.requestData('2fa/verify', undefined, 'post', undefined, {
      token,
      temp_token: tempToken,
      trust_device: trustDevice,
      device_name: deviceName,
    });

    // Store the actual auth token after successful 2FA verification
    sessionStorage.setItem(STORAGE_DOQBOARD_TOKEN, result.token);
    sessionStorage.setItem(STORAGE_USER_ID, result.user.id);

    // Remove temp token
    sessionStorage.removeItem('2fa-temp-token');

    return result.user;
  },

  /**
   * Admin: Disable 2FA for a specific user
   * @param {number} userId - ID of the user
   * @returns {Promise<Object>} Result of disable operation
   */
  adminDisable: async (userId) => (
    api.partial_update('users', userId, { two_factor_enabled: false }, { admin: true })
  ),
};

export default twoFactorService;
